import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { ParsedParam } from "@/lib/mcp/schema";
import { Settings } from "lucide-react";
import { ExecutionResults } from "../shared/components";
import { ToolParameterForm } from "./ToolParameterForm";
import { useToolExecution } from "./useToolExecution";

export interface ToolExecutionPanelProps {
  sessionId: string;
  toolName: string | null;
  parsedParams: ParsedParam[];
  configureSectionState: "pending" | "active" | "completed" | "invalidated";
}

export function ToolExecutionPanel({
  sessionId,
  toolName,
  parsedParams,
  configureSectionState,
}: ToolExecutionPanelProps) {
  const { isLoading, result, error, executeTool, canExecute } =
    useToolExecution({
      sessionId,
      toolName,
      parsedParams,
    });

  if (!toolName) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Select a tool above to configure parameters and execute</p>
      </div>
    );
  }

  const showParametersChanged =
    configureSectionState === "invalidated" && !!(result || error);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium">Parameters</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <ToolParameterForm
            toolName={toolName}
            parsedParams={parsedParams}
            onExecute={executeTool}
            isExecuting={isLoading}
            canExecute={canExecute}
            showParametersChanged={showParametersChanged}
          />

          <ExecutionResults
            isLoading={isLoading}
            result={result}
            error={error}
          />
        </CardContent>
      </Card>
    </>
  );
}
